"use client";

import type { MuxPlayerRefAttributes } from "@mux/mux-player-react";
import type { MotionValue } from "framer-motion";
import type { CSSProperties } from "react";
import * as React from "react";

import { MuxPlayerEager } from "@/components/post-rich-text/mux-player-eager";
import { useIntersectedOnce } from "@/hooks/use-intersection-once";
import { cn } from "@/lib/utils";

import type { ProductPreviewVideo } from "./product-preview-videos";
import { PRODUCT_PREVIEW_DESKTOP_VIDEOS } from "./product-preview-videos";

const PLAYER_STYLE = {
  width: "100%",
  height: "100%",
  aspectRatio: "auto",
  objectFit: "cover",
  "--media-object-fit": "cover",
  // object-position is owned by globals.css — desktop uses container queries;
  // mobile-source clips use product-preview-video-cover--mobile (left top).
  "--media-background-color": "#fbfbfb",
  "--controls": "none",
} as CSSProperties &
  Record<
    | "--media-object-fit"
    | "--media-background-color"
    | "--controls"
    | "--media-object-position"
    | "--media-transform-origin"
    | "--media-transform-scale",
    string
  >;

const VIDEO_REVEAL_MS = 400;

export type ProductPreviewVideoPanelHandle = {
  /** Play (or restart) the clip for a tab — call synchronously from click handlers. */
  playTab: (index: number) => void;
};

interface ProductPreviewVideoPanelProps {
  active: number;
  /** Bumps on every tab click so the active clip restarts even when re-selected. */
  replayKey: number;
  progress: MotionValue<number>;
  reducedMotion: boolean;
  /** When false, players stay paused and onEnded is ignored (hidden duplicate panel). */
  playbackEnabled?: boolean;
  onAdvance: () => void;
  className?: string;
  videos?: readonly ProductPreviewVideo[];
  /** Mobile-source clips vs desktop crop in the mobile card frame. */
  videoCrop?: "mobile" | "desktop";
  /** Per-tab object-position overrides (mobile-source sm crops). */
  tabObjectPositions?: readonly (string | undefined)[];
  /** Per-tab scale overrides (mobile-source sm crops). */
  tabScales?: readonly (number | undefined)[];
}

function playFromBeginning(player: MuxPlayerRefAttributes) {
  const attemptPlay = (retriesLeft = 4) => {
    void player.play().catch(() => {
      if (retriesLeft <= 0) return;
      window.setTimeout(() => attemptPlay(retriesLeft - 1), 250);
    });
  };

  const restart = () => {
    // iOS Safari does not fire "seeked" when currentTime is already 0 — calling
    // play() directly avoids a silent no-op on the first tab at page load.
    if (player.currentTime <= 0.05) {
      attemptPlay();
      return;
    }

    player.pause();
    player.addEventListener("seeked", () => attemptPlay(), { once: true });
    player.currentTime = 0;
  };

  if (player.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
    restart();
    return;
  }

  player.addEventListener("loadeddata", restart, { once: true });
  player.addEventListener("canplay", () => attemptPlay(), { once: true });
}

function ProductPreviewVideoLayer({
  video,
  index,
  isActive,
  reducedMotion,
  registerPlayer,
  onEnded,
  videoCrop,
  tabObjectPosition,
  tabScale,
}: {
  video: ProductPreviewVideo;
  index: number;
  isActive: boolean;
  reducedMotion: boolean;
  registerPlayer: (
    index: number,
    player: MuxPlayerRefAttributes | null
  ) => void;
  onEnded: () => void;
  videoCrop: "mobile" | "desktop";
  tabObjectPosition?: string;
  tabScale?: number;
}) {
  const [isPlaying, setIsPlaying] = React.useState(false);
  const mobileObjectPosition = tabObjectPosition ?? "left top";
  const mobileTransformOrigin =
    tabObjectPosition?.includes("center") === true ||
    tabObjectPosition?.includes("%") === true
      ? mobileObjectPosition
      : "top left";
  const mobileTransformScale = tabScale ?? 1.02;

  React.useEffect(() => {
    if (!isActive) {
      setIsPlaying(false);
    }
  }, [isActive]);

  const shouldAutoplay = isActive && !reducedMotion;
  const mobileCoverStyle =
    videoCrop === "mobile"
      ? {
          objectFit: "cover" as const,
          objectPosition: mobileObjectPosition,
        }
      : undefined;

  return (
    <div
      className={cn(
        "pointer-events-none absolute inset-0 overflow-hidden transition-opacity duration-200 ease-out",
        isActive ? "z-10 opacity-100" : "z-0 opacity-0"
      )}
    >
      <MuxPlayerEager
        ref={(instance) => registerPlayer(index, instance)}
        chromeless
        playbackId={video.playbackId}
        poster=""
        title={`Onboarding step ${index + 1}`}
        muted
        playsInline
        autoPlay={shouldAutoplay ? "muted" : undefined}
        preload={isActive ? "auto" : "none"}
        minResolution={videoCrop === "mobile" ? undefined : "1080p"}
        renditionOrder="desc"
        loop={false}
        streamType="on-demand"
        className={cn(
          "product-preview-video-cover pointer-events-none absolute inset-0 transition-opacity ease-out",
          isPlaying ? "opacity-100" : "opacity-0",
          videoCrop === "mobile"
            ? "product-preview-video-cover--mobile"
            : "product-preview-video-bleed"
        )}
        style={{
          transitionDuration: reducedMotion ? "0ms" : `${VIDEO_REVEAL_MS}ms`,
          ...PLAYER_STYLE,
          ...(videoCrop === "mobile"
            ? {
                objectFit: "cover",
                objectPosition: mobileObjectPosition,
                "--media-object-fit": "cover",
                "--media-object-position": mobileObjectPosition,
                "--media-transform-origin": mobileTransformOrigin,
                "--media-transform-scale": String(mobileTransformScale),
                ...mobileCoverStyle,
              }
            : {}),
        }}
        onPlaying={() => setIsPlaying(true)}
        onPause={() => {
          if (!isActive) setIsPlaying(false);
        }}
        onEnded={onEnded}
      />
    </div>
  );
}

export const ProductPreviewVideoPanel = React.forwardRef<
  ProductPreviewVideoPanelHandle,
  ProductPreviewVideoPanelProps
>(function ProductPreviewVideoPanel(
  {
    active,
    replayKey,
    progress,
    reducedMotion,
    playbackEnabled = true,
    onAdvance,
    className,
    videos = PRODUCT_PREVIEW_DESKTOP_VIDEOS,
    videoCrop = "desktop",
    tabObjectPositions,
    tabScales,
  },
  ref
) {
  const panelRef = React.useRef<HTMLDivElement>(null);
  const inView = useIntersectedOnce(panelRef, { threshold: 0.25 });
  const playerRefs = React.useRef<(MuxPlayerRefAttributes | null)[]>([]);
  const activeRef = React.useRef(active);
  activeRef.current = active;
  const inViewRef = React.useRef(inView);
  inViewRef.current = inView;
  const playbackEnabledRef = React.useRef(playbackEnabled);
  playbackEnabledRef.current = playbackEnabled;
  const reducedMotionRef = React.useRef(reducedMotion);
  reducedMotionRef.current = reducedMotion;

  const registerPlayer = React.useCallback(
    (index: number, player: MuxPlayerRefAttributes | null) => {
      playerRefs.current[index] = player;

      // Mux refs attach after useLayoutEffect — kick off the first clip once
      // the active player element exists (initial load + layout panel swap).
      if (
        player &&
        playbackEnabledRef.current &&
        !reducedMotionRef.current &&
        index === activeRef.current
      ) {
        playFromBeginning(player);
      }
    },
    []
  );

  const syncActivePlayback = React.useCallback(
    (targetIndex: number) => {
      if (!playbackEnabled || reducedMotion) {
        playerRefs.current.forEach((player) => player?.pause());
        return;
      }

      playerRefs.current.forEach((player, index) => {
        if (!player) return;

        if (index === targetIndex) {
          playFromBeginning(player);
          return;
        }

        player.pause();
        if (player.currentTime > 0) {
          player.currentTime = 0;
        }
      });
    },
    [playbackEnabled, reducedMotion]
  );

  React.useImperativeHandle(
    ref,
    () => ({
      playTab: (index: number) => syncActivePlayback(index),
    }),
    [syncActivePlayback]
  );

  // Auto-advance and initial mount — layout effect is fine for muted autoplay.
  React.useLayoutEffect(() => {
    syncActivePlayback(active);
  }, [active, replayKey, syncActivePlayback]);

  // iOS Safari often blocks the first programmatic play until the panel intersects.
  React.useEffect(() => {
    if (!inView || !playbackEnabled || reducedMotion) return;
    syncActivePlayback(activeRef.current);
  }, [inView, playbackEnabled, reducedMotion, syncActivePlayback]);

  // rAF-driven progress keeps the tab indicator smooth (timeupdate is ~4 Hz).
  React.useEffect(() => {
    if (reducedMotion || !playbackEnabled) return;

    let rafId = 0;
    const tick = () => {
      const player = playerRefs.current[active];
      if (player && player.duration && !player.paused && !document.hidden) {
        progress.set(
          Math.min(1, Math.max(0, player.currentTime / player.duration))
        );
      }
      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [active, playbackEnabled, progress, reducedMotion]);

  React.useEffect(() => {
    if (reducedMotion) {
      playerRefs.current.forEach((player) => player?.pause());
      progress.set(0);
    }
  }, [progress, reducedMotion]);

  const [mountedTabIndexes, setMountedTabIndexes] = React.useState(
    () => new Set<number>([active])
  );

  React.useEffect(() => {
    setMountedTabIndexes((current) => {
      if (current.has(active)) return current;
      const next = new Set(current);
      next.add(active);
      return next;
    });
  }, [active]);

  // Hidden duplicate panels (mobile vs desktop) must not mount Mux streams —
  // iOS Safari chokes when six HLS players preload at once.
  if (!playbackEnabled) {
    return (
      <div
        className={cn(
          "@container relative h-[622px] overflow-hidden border-b border-[#e5e6e6] bg-[#fbfbfb]",
          className
        )}
        aria-hidden
      />
    );
  }

  return (
    <div
      ref={panelRef}
      className={cn(
        "@container relative h-[622px] overflow-hidden border-b border-[#e5e6e6] bg-[#fbfbfb]",
        className
      )}
      role="img"
      aria-label={`Product preview: step ${active + 1} of ${videos.length}`}
    >
      {videos.map((video, index) =>
        mountedTabIndexes.has(index) ? (
          <ProductPreviewVideoLayer
            key={video.playbackId}
            video={video}
            index={index}
            isActive={active === index}
            reducedMotion={reducedMotion}
            registerPlayer={registerPlayer}
            videoCrop={videoCrop}
            tabObjectPosition={tabObjectPositions?.[index]}
            tabScale={tabScales?.[index]}
            onEnded={() => {
              if (
                !playbackEnabled ||
                activeRef.current !== index ||
                reducedMotion
              ) {
                return;
              }
              onAdvance();
            }}
          />
        ) : null
      )}
    </div>
  );
});
